import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Check, ScanLine, FolderSync, Truck, Sparkles,
  FileSpreadsheet, ShoppingCart, BarChart3, Lock, Quote,
  Boxes, MessageSquare, Tag, ChevronRight, Star
} from 'lucide-react';
import WaitlistModal from '@/components/WaitlistModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Landing page — public face of RetailMaster (retailmaster.no)              */
/* -------------------------------------------------------------------------- */

const FEATURES = [
  {
    icon: ScanLine,
    title: 'Periodisk varetelling',
    body: 'Tell varene gjennom året, ikke alt på én gang. Automatiske ukesplaner, framdriftskort og full historikk per kvalitet og lokasjon.',
  },
  {
    icon: FolderSync,
    title: 'WooCommerce-synk',
    body: 'Toveis synk mellom butikkdata og nettbutikken din. SKU, EAN, lager og priser holdes alltid i takt.',
  },
  {
    icon: Truck,
    title: 'Varemottak og ordrer',
    body: 'Last opp pakkseddelen, match mot bestilling, registrer rest. Aldri mer sjekke om varene faktisk kom.',
  },
  {
    icon: Sparkles,
    title: 'Leverandør-AI',
    body: 'Lim inn en e-post fra leverandøren — AI plukker ut nye priser, kampanjer, utgåtte varer og merker dem direkte i systemet.',
  },
  {
    icon: FileSpreadsheet,
    title: 'Excel-import',
    body: 'Skreddersydde parsere for hver leverandør. Slipp manuelle copy-paste-ritualer hver eneste sesong.',
  },
  {
    icon: ShoppingCart,
    title: 'Bestillingshåndtering',
    body: 'Kundebestillinger, restordrer og lager-bestillinger på ett sted — koblet mot kassesystemet.',
  },
  {
    icon: BarChart3,
    title: 'Rapporter og innsikt',
    body: 'Periodisk lagerverdi, sviksoversikt og rapporter for regnskapet. Tall som faktisk stemmer.',
  },
  {
    icon: Lock,
    title: 'Sikkerhet i bunn',
    body: 'Bcrypt-hashet innlogging, JWT-beskyttet API og daglig backup. Driftet på norsk infrastruktur.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Koble til WooCommerce',
    body: 'Lim inn API-nøkkelen og la RetailMaster trekke inn produkter, kategorier og lager. Tar mindre enn 5 minutter.',
  },
  {
    n: '02',
    title: 'Importer leverandørdata',
    body: 'Last opp Excel-filer, scrap nettsider eller bruk AI-parser. Alle leverandører – ett system.',
  },
  {
    n: '03',
    title: 'Drift butikken som vanlig',
    body: 'Ta varemottak, kjør varetelling, ekspeder ordrer. RetailMaster holder tallene synkrone i bakgrunnen.',
  },
];

const PRICING = [
  {
    name: 'Start',
    price: '999',
    suffix: 'kr / mnd',
    onboarding: '2 999 kr oppstart · inkl. 1 time opplæring',
    blurb: 'For deg som driver én butikk og vil ha tallene på plass.',
    features: [
      'Inntil 500 varer pr. måned',
      'Import av varer og garnpakker',
      'Varetelling-modul',
      'Ordrebekreftelse',
      'Varemottak',
      'Varer-i-bestilling',
      'Bestillingsliste',
      'Lageroversikt',
      'Ubegrenset antall brukere',
    ],
    cta: 'Velg Start',
    href: '/login',
    highlight: false,
  },
  {
    name: 'Pro',
    price: '1 999',
    suffix: 'kr / mnd',
    onboarding: '2 999 kr oppstart · inkl. 2 timer opplæring',
    blurb: 'Den vanligste pakken — for butikker som vil automatisere mer.',
    features: [
      'Alt i Start, pluss:',
      'Inntil 1 000 varer pr. måned',
      'Katalog­import',
      'Reklamasjon og modellstrikk',
      'Samsvar med PCK',
      'Ubegrenset antall brukere',
    ],
    cta: 'Velg Pro',
    href: '/login',
    highlight: true,
  },
  {
    name: 'Full pakke',
    price: '4 499',
    suffix: 'kr / mnd',
    onboarding: '2 999 kr oppstart · inkl. 2 timer opplæring',
    blurb: 'Alt RetailMaster har — for kjeder og butikker som krever full kontroll.',
    features: [
      'Alt i Pro, pluss:',
      'Ubegrenset antall varer',
      'Periodisk varetelling',
      'Tilleggsbutikker fra 2 499 kr / butikk',
      'Alle moduler og integrasjoner',
      'Ubegrenset antall brukere',
    ],
    cta: 'Velg Full pakke',
    href: '/login',
    highlight: false,
  },
];

const FAQ = [
  {
    q: 'Hvilke kassesystemer fungerer det med?',
    a: 'Foreløpig er RetailMaster bygget mot PCK. Vi jobber med kontakt mot flere kassesystemer, og legger til integrasjoner fortløpende. Ta kontakt om du ønsker en spesifikk integrasjon.',
  },
  {
    q: 'Må jeg flytte data fra nettbutikken min?',
    a: 'Nei. RetailMaster legger seg ovenpå eksisterende WooCommerce og holder seg synkron. Du fortsetter å bruke nettbutikken som før.',
  },
  {
    q: 'Hvor lagres dataene mine?',
    a: 'På servere i EU. Daglig backup, bcrypt-hashing av passord og JWT-beskyttet API. Vi gir deg en eksport når som helst.',
  },
  {
    q: 'Kan jeg bytte plan eller si opp?',
    a: 'Ja. Månedlig fakturering, ingen bindingstid. Bytt plan eller si opp inne i Innstillinger.',
  },
  {
    q: 'Passer det for andre enn garnbutikker?',
    a: 'Foreløpig er RetailMaster utviklet for garnbutikker, men det kan fint brukes for andre butikker også. Vi jobber kontinuerlig med utvikling og tilpasninger basert på tilbakemeldinger.',
  },
];

export default function Landing() {
  const [year] = useState(new Date().getFullYear());
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('Pro');
  const [content, setContent] = useState(null);

  // Fetch editable landing content (CMS); fall back to bundled defaults if it fails
  useEffect(() => {
    axios.get(`${API}/landing-content`)
      .then(r => { if (r.data?.content) setContent(r.data.content); })
      .catch(() => { /* keep defaults */ });
  }, []);

  const openWaitlist = (plan) => {
    setSelectedPlan(plan || 'Pro');
    setWaitlistOpen(true);
  };

  // Merge CMS content over defaults so missing keys still render
  const hero = (content && content.hero) || {};
  const testimonial = (content && content.testimonial) || {};
  const cmsPricing = (content && Array.isArray(content.pricing) && content.pricing.length) ? content.pricing : PRICING;
  const cmsFaq = (content && Array.isArray(content.faq) && content.faq.length) ? content.faq : FAQ;
  const pricingNote = (content && content.pricing_note)
    || 'Alle priser er oppgitt eks. mva. Faktureres månedlig. Oppstartsavgiften bortfaller ved årsabonnement.';

  // Parallax for hero subtle effect
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground antialiased" data-testid="landing-page">

      {/* ============== Top nav ============== */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/75 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5 group" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm shadow-sm">
              RM
            </div>
            <span className="text-[15px] font-semibold tracking-tight">RetailMaster</span>
          </a>

          <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Funksjoner</a>
            <a href="#how" className="hover:text-foreground transition-colors">Hvordan</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Priser</a>
            <a href="#venteliste" className="hover:text-foreground transition-colors">Venteliste</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </nav>

          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium
              bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-200 ease-out
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
              active:scale-[0.98]"
            data-testid="landing-login-button"
          >
            Logg inn
            <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </div>
      </header>

      {/* ============== HERO ============== */}
      <section id="top" className="relative overflow-hidden">
        {/* Decorative gradient blobs (warm, subtle) */}
        <div
          aria-hidden
          className="absolute -top-40 -right-32 w-[640px] h-[640px] rounded-full opacity-[0.08] blur-3xl"
          style={{ background: 'radial-gradient(circle, hsl(25 60% 35%) 0%, transparent 70%)', transform: `translateY(${scrollY * 0.15}px)` }}
        />
        <div
          aria-hidden
          className="absolute top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-[0.06] blur-3xl"
          style={{ background: 'radial-gradient(circle, hsl(35 80% 50%) 0%, transparent 70%)' }}
        />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card text-xs text-muted-foreground mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />
              Norsk butikkdrift, profesjonalisert
            </div>

            <h1 className="text-[2.75rem] leading-[1.05] sm:text-6xl lg:text-7xl font-semibold tracking-tight">
              {hero.title_line1 || 'Operativsystemet for butikker som selger'}
              <span className="text-primary">{hero.title_accent || ' fysisk og på nett.'}</span>
            </h1>

            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {hero.subtitle || 'RetailMaster er én plattform som binder sammen kasse, WooCommerce, leverandører og lager — slik at tallene alltid stemmer og du kan fokusere på kundene i stedet for regnearkene.'}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => openWaitlist('Pro')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium
                  bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm
                  transition-colors duration-200 ease-out active:scale-[0.98]
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                data-testid="hero-cta-primary"
              >
                {hero.primary_cta || 'Bli med på venteliste'}
                <ArrowRight size={16} strokeWidth={2} />
              </button>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium
                  bg-card border border-border hover:bg-secondary text-foreground
                  transition-colors duration-200 ease-out"
                data-testid="hero-cta-secondary"
              >
                {hero.secondary_cta || 'Se hva det gjør'}
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5"><Check size={13} className="text-success" strokeWidth={2.25} /> Ingen bindingstid</div>
              <div className="flex items-center gap-1.5"><Check size={13} className="text-success" strokeWidth={2.25} /> Norsk støtte</div>
              <div className="flex items-center gap-1.5"><Check size={13} className="text-success" strokeWidth={2.25} /> Daglig backup</div>
              <div className="flex items-center gap-1.5"><Check size={13} className="text-success" strokeWidth={2.25} /> EU-hosted</div>
            </div>
          </div>

          {/* Hero preview / mockup card */}
          <div className="mt-16 relative">
            <div className="relative rounded-2xl border border-border bg-card shadow-[0_24px_64px_rgba(30,25,20,0.08),0_2px_8px_rgba(30,25,20,0.04)] overflow-hidden"
                 data-testid="hero-preview">
              {/* faux app chrome */}
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border bg-secondary/40">
                <span className="w-2.5 h-2.5 rounded-full bg-destructive/50" />
                <span className="w-2.5 h-2.5 rounded-full bg-warning/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-success/60" />
                <span className="ml-3 text-[11px] text-muted-foreground font-mono">retailmaster.no/periodisk-varetelling</span>
              </div>

              <div className="grid grid-cols-12 min-h-[420px]">
                {/* Faux sidebar */}
                <aside className="hidden md:flex col-span-3 lg:col-span-2 flex-col gap-1 bg-sidebar p-4 border-r border-sidebar-border">
                  <div className="text-[9px] uppercase tracking-[0.15em] font-semibold text-muted-foreground/70 mb-1.5">Lager</div>
                  <div className="px-2.5 py-1.5 rounded-md text-[12px] bg-primary text-primary-foreground font-medium flex items-center gap-2">
                    <ScanLine size={12} strokeWidth={2} /> Varetelling
                  </div>
                  <div className="px-2.5 py-1.5 rounded-md text-[12px] text-sidebar-foreground hover:bg-sidebar-accent flex items-center gap-2">
                    <Boxes size={12} strokeWidth={1.75} /> Produktmaster
                  </div>
                  <div className="px-2.5 py-1.5 rounded-md text-[12px] text-sidebar-foreground hover:bg-sidebar-accent flex items-center gap-2">
                    <Truck size={12} strokeWidth={1.75} /> Varemottak
                  </div>
                  <div className="px-2.5 py-1.5 rounded-md text-[12px] text-sidebar-foreground hover:bg-sidebar-accent flex items-center gap-2">
                    <FolderSync size={12} strokeWidth={1.75} /> Synk
                  </div>
                  <div className="px-2.5 py-1.5 rounded-md text-[12px] text-sidebar-foreground hover:bg-sidebar-accent flex items-center gap-2">
                    <Tag size={12} strokeWidth={1.75} /> Salg
                  </div>
                </aside>

                {/* Faux content */}
                <div className="col-span-12 md:col-span-9 lg:col-span-10 p-6 lg:p-8 space-y-5">
                  <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">Lagerstyring</div>
                      <h3 className="text-xl lg:text-2xl font-semibold tracking-tight mt-0.5">Periodisk varetelling</h3>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-full bg-success/10 text-success font-semibold">Live</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2 bg-background rounded-xl p-4 border border-border">
                      <div className="text-[9px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">Framdrift i år</div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-2xl lg:text-3xl font-semibold tabular-nums">3 412</span>
                        <span className="text-sm text-muted-foreground tabular-nums">/ 4 546</span>
                      </div>
                      <div className="mt-3 h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: '75%' }} />
                      </div>
                    </div>
                    <div className="bg-background rounded-xl p-4 border border-border">
                      <div className="text-[9px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">Denne uka</div>
                      <div className="mt-1 text-2xl lg:text-3xl font-semibold tabular-nums">128</div>
                      <div className="mt-1 text-[11px] text-success font-medium">↑ 12 % over mål</div>
                    </div>
                  </div>

                  <div className="bg-background rounded-xl border border-border overflow-hidden">
                    <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground border-b border-border bg-secondary/30">
                      <div className="col-span-6">Kvalitet</div>
                      <div className="col-span-3">Leverandør</div>
                      <div className="col-span-3 text-right">Status</div>
                    </div>
                    {[
                      ['Smart', 'Sandnes Garn', '12 / 12'],
                      ['Alpakka Silke', 'Sandnes Garn', '8 / 14'],
                      ['Tilia', 'Filcolana', '24 / 24'],
                      ['Tinde Pelsull', 'Hillesvåg', '6 / 18'],
                    ].map(([k, l, s], i) => {
                      const done = s.split(' / ')[0] === s.split(' / ')[1];
                      return (
                        <div key={i} className="grid grid-cols-12 px-4 py-3 text-[12px] border-b border-border/60 last:border-b-0 items-center">
                          <div className="col-span-6 font-medium text-foreground">{k}</div>
                          <div className="col-span-3 text-muted-foreground">{l}</div>
                          <div className={`col-span-3 text-right font-mono tabular-nums ${done ? 'text-success' : 'text-warning'}`}>{s}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Logo strip / trust ============== */}
      <section className="py-10 border-y border-border/60 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-center text-xs uppercase tracking-[0.18em] text-muted-foreground/80 mb-6">
            Bygget for integrasjon med
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-sm font-medium text-muted-foreground">
            <span>WooCommerce</span>
            <span className="opacity-50">·</span>
            <span>Samspill Kasse</span>
            <span className="opacity-50">·</span>
            <span>Sandnes Garn</span>
            <span className="opacity-50">·</span>
            <span>Rauma Garn</span>
            <span className="opacity-50">·</span>
            <span>Hillesvåg Ullvarefabrikk</span>
            <span className="opacity-50">·</span>
            <span>Filcolana</span>
            <span className="opacity-50">·</span>
            <span>Isager</span>
            <span className="opacity-50">·</span>
            <span>Villy Jensen</span>
            <span className="opacity-50">·</span>
            <span>Kort &amp; Godt</span>
            <span className="opacity-50">·</span>
            <span>Marte Helgetun</span>
          </div>
        </div>
      </section>

      {/* ============== Features ============== */}
      <section id="features" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-primary mb-3">
              Funksjoner
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Alt du trenger for å drifte butikken — uten å lime sammen seks ulike verktøy.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="group bg-card p-7 hover:bg-secondary/40 transition-colors duration-200 ease-out">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                    <Icon size={18} strokeWidth={1.75} />
                  </div>
                  <h3 className="text-[15px] font-semibold tracking-tight mb-1.5">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== How it works ============== */}
      <section id="how" className="py-24 lg:py-28 bg-secondary/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-primary mb-3">
              Slik fungerer det
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Fra kaos til kontroll på en ettermiddag.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {STEPS.map((s, i) => (
              <div key={i} className="relative bg-card border border-border rounded-2xl p-7 shadow-[0_1px_2px_rgba(30,25,20,0.04)]">
                <div className="text-[11px] font-mono font-semibold tracking-[0.15em] text-primary mb-4">
                  {s.n}
                </div>
                <h3 className="text-lg font-semibold tracking-tight mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                {i < STEPS.length - 1 && (
                  <ChevronRight
                    size={20}
                    strokeWidth={1.5}
                    className="hidden md:block absolute top-1/2 -right-5 -translate-y-1/2 text-border"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== Quote / testimonial ============== */}
      <section className="py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="bg-card border border-border rounded-2xl p-10 lg:p-14 relative overflow-hidden">
            <div aria-hidden className="absolute -top-6 -left-2 text-primary/10">
              <Quote size={140} strokeWidth={1} />
            </div>
            <div className="relative">
              <div className="flex items-center gap-0.5 mb-5 text-warning">
                {[1,2,3,4,5].map(n => <Star key={n} size={16} fill="currentColor" strokeWidth={0} />)}
              </div>
              <blockquote className="text-xl lg:text-2xl tracking-tight font-medium leading-snug">
                «{testimonial.quote || 'Vi gikk fra å bruke 10–15 minutter på å lage hver garnpakke til 1,5 minutt. Og nå har jeg full kontroll på restvarer, ting jeg må bestille og hva kundene venter på.'}»
              </blockquote>
              <div className="mt-7 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center font-semibold text-sm">{testimonial.initials || 'IM'}</div>
                <div>
                  <div className="text-sm font-semibold">{testimonial.name || 'Inger-Marie Holmsen'}</div>
                  <div className="text-xs text-muted-foreground">{testimonial.role || 'Eier, Titt Inn Garn'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Closed beta / Waitlist section ============== */}
      <section id="venteliste" className="py-20 lg:py-24 bg-secondary/30 border-y border-border">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-primary mb-3">
            Venteliste
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
            Bli med i closed beta.
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
            Vi slipper inn 10 butikker av gangen for å sikre kvaliteten på oppsett og support.
            Tidlige medlemmer får lifetime-rabatt når vi går live.
          </p>
          <button
            type="button"
            onClick={() => openWaitlist('Pro')}
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-medium
              bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm
              transition-colors duration-200 ease-out active:scale-[0.98]
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            data-testid="waitlist-section-cta"
          >
            Meld meg på
            <ArrowRight size={15} strokeWidth={2} />
          </button>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5"><Check size={13} className="text-success" strokeWidth={2.25} /> Lifetime-rabatt</div>
            <div className="flex items-center gap-1.5"><Check size={13} className="text-success" strokeWidth={2.25} /> Ingen bindingstid</div>
            <div className="flex items-center gap-1.5"><Check size={13} className="text-success" strokeWidth={2.25} /> 1-til-1 onboarding</div>
          </div>
        </div>
      </section>

      {/* ============== Pricing ============== */}
      <section id="pricing" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-primary mb-3">
              Priser
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Enkle planer. Si opp når som helst.
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              {pricingNote}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {cmsPricing.map((p, i) => (
              <div
                key={i}
                className={`relative rounded-2xl p-8 flex flex-col ${
                  p.highlight
                    ? 'bg-foreground text-background border-2 border-primary shadow-[0_24px_64px_rgba(15,23,42,0.12)]'
                    : 'bg-card text-foreground border border-border shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
                }`}
                data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-8 text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1 rounded-full bg-primary text-primary-foreground">
                    Mest populær
                  </div>
                )}

                <div className="text-sm font-semibold tracking-tight mb-1">{p.name}</div>
                <div className={`text-xs mb-6 ${p.highlight ? 'text-background/60' : 'text-muted-foreground'}`}>
                  {p.blurb}
                </div>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl lg:text-5xl font-semibold tracking-tight tabular-nums">{p.price}</span>
                  {p.suffix && (
                    <span className={`text-sm ${p.highlight ? 'text-background/60' : 'text-muted-foreground'}`}>
                      {p.suffix}
                    </span>
                  )}
                </div>
                {p.onboarding ? (
                  <div className={`text-xs mb-7 ${p.highlight ? 'text-background/60' : 'text-muted-foreground'}`}>
                    + {p.onboarding}
                  </div>
                ) : <div className="mb-7" />}

                <ul className="space-y-3 mb-8 flex-1">
                  {(p.features || []).map((feat, fi) => (
                    <li key={fi} className="flex items-start gap-2.5 text-sm">
                      <Check size={15} strokeWidth={2.25} className={`mt-0.5 shrink-0 ${p.highlight ? 'text-primary' : 'text-success'}`} />
                      <span className={p.highlight ? 'text-background/90' : 'text-foreground'}>{feat}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => openWaitlist(p.name)}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ease-out active:scale-[0.98]
                    ${p.highlight
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-foreground text-background hover:bg-foreground/90'}`}
                  data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {p.cta || 'Bli med på venteliste'}
                  <ArrowRight size={14} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section id="faq" className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="mb-12">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-primary mb-3">
              FAQ
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Spørsmål andre har stilt.
            </h2>
          </div>

          <div className="divide-y divide-border border-y border-border">
            {cmsFaq.map((item, i) => (
              <details key={i} className="group py-5" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between gap-6 cursor-pointer list-none">
                  <span className="text-[15px] font-medium tracking-tight">{item.q}</span>
                  <span className="shrink-0 w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground transition-transform duration-200 group-open:rotate-45 group-open:bg-primary group-open:text-primary-foreground group-open:border-primary">
                    <span className="text-base leading-none">+</span>
                  </span>
                </summary>
                <p className="mt-3 pr-12 text-sm text-muted-foreground leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============== Final CTA ============== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-foreground text-background p-10 lg:p-16">
            <div aria-hidden className="absolute -top-32 -right-20 w-[480px] h-[480px] rounded-full opacity-20 blur-3xl"
                 style={{ background: 'radial-gradient(circle, hsl(25 60% 35%) 0%, transparent 70%)' }} />
            <div className="relative max-w-2xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                Klar til å rydde i butikkdriften?
              </h2>
              <p className="mt-4 text-base lg:text-lg text-background/70 leading-relaxed">
                Logg inn og koble til WooCommerce på under 5 minutter. Eller send en e-post — vi svarer samme dag.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openWaitlist('Pro')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium
                    bg-primary text-primary-foreground hover:bg-primary/90
                    transition-colors duration-200 ease-out active:scale-[0.98]"
                  data-testid="final-cta-waitlist"
                >
                  Bli med på venteliste
                  <ArrowRight size={15} strokeWidth={2} />
                </button>
                <a
                  href="mailto:hei@retailmaster.no"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium
                    bg-background/10 hover:bg-background/15 backdrop-blur text-background border border-background/20
                    transition-colors duration-200 ease-out"
                  data-testid="final-cta-contact"
                >
                  <MessageSquare size={15} strokeWidth={1.75} />
                  Kontakt oss
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Footer ============== */}
      <footer className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">RM</div>
                <span className="text-[15px] font-semibold tracking-tight">RetailMaster</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
                Operativsystemet for butikker som selger fysisk og på nett. Bygget i Norge av Holmsen Design.
              </p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground/80 mb-3">Produkt</div>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-foreground hover:text-primary transition-colors">Funksjoner</a></li>
                <li><a href="#pricing" className="text-foreground hover:text-primary transition-colors">Priser</a></li>
                <li><a href="#faq" className="text-foreground hover:text-primary transition-colors">FAQ</a></li>
                <li><Link to="/login" className="text-foreground hover:text-primary transition-colors">Logg inn</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground/80 mb-3">Kontakt</div>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:hei@retailmaster.no" className="text-foreground hover:text-primary transition-colors">hei@retailmaster.no</a></li>
                <li><a href="https://retailmaster.no" className="text-foreground hover:text-primary transition-colors">retailmaster.no</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
            <div>&copy; {year} Holmsen Design. Alle rettigheter forbeholdt.</div>
            <div className="flex items-center gap-5">
              <span>Personvern</span>
              <span>Vilkår</span>
              <span>Status</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Waitlist modal */}
      <WaitlistModal
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        defaultPlan={selectedPlan}
      />
    </div>
  );
}
